import { withNamespaces } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
    Button,
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    Col,
    Row,
    Spinner
} from "reactstrap";
import { toast } from "react-toastify";
import DateUtils from "src/services/utils/DateUtils";
import { INTERNATIONAL_DATE_FORMAT } from "src/common/constants";
import DataProtectionSoftwareService from "src/modules/data-protection/apis/DataProtectionSoftwareService";
import { SOFTWARE_OVERVIEW_QUERY_KEY } from "../../hooks/useSoftwareOverview";
import { getPrivacyRiskGauge } from "../../constants/riskGauge";
import { ASSESSMENT_STATUS_BADGES } from "../../constants/statusBadges";
import {
    getAssessmentStatusItems,
    getPrivacyRiskScore
} from "../../helpers/assessments";

const StatusBadge = ({ t, status }) => {
    const config = ASSESSMENT_STATUS_BADGES[status];

    if (!config) {
        return null;
    }

    return (
        <span
            className="overview-ai-summary__verification-status"
            style={{
                color: config.color,
                backgroundColor: config.backgroundColor
            }}
        >
            <i className={`${config.icon} me-1`} />
            {t(config.label)}
        </span>
    );
};

const AiSummary = ({
    t,
    softwareData,
    core = {},
    ai = {},
    assessments = {},
    degraded = false
}) => {
    const history = useHistory();
    const queryClient = useQueryClient();
    const dateUtils = new DateUtils();
    const softwareId = softwareData?.id;
    const summary = ai.summary || {};
    const privacyRiskAnalysis = ai.privacyRiskAnalysis || {};
    const createdOn = core.createdAt || core.created_at;

    const generateMutation = useMutation({
        mutationFn: async ({ type, force = false }) => {
            const service = DataProtectionSoftwareService.getInstance();

            return await service.generateComplianceAi(softwareId, {
                type,
                force
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries([
                SOFTWARE_OVERVIEW_QUERY_KEY,
                softwareId
            ]);
        },
        onError: (error) => {
            if (process.env.NODE_ENV === "development") {
                console.error(error);
            }

            toast(t("An error occurred while generating the AI summary."), {
                type: "error"
            });
        }
    });

    const generatingType = generateMutation.isLoading
        ? generateMutation.variables?.type
        : null;
    const summaryParagraphs = [summary.summary].filter(Boolean);
    const highlights = summary.highlights || [];
    const riskLevel =
        privacyRiskAnalysis.riskLevel ||
        assessments.processingActivity?.privacyRisk?.level ||
        "not_assessed";
    const gauge = getPrivacyRiskGauge(riskLevel);
    const privacyScore = getPrivacyRiskScore(
        assessments.processingActivity?.privacyRisk?.level || riskLevel
    );
    const isNotAssessed = riskLevel === "not_assessed";
    const generatedAt =
        summary.generatedAt || privacyRiskAnalysis.generatedAt;
    const assessmentItems = getAssessmentStatusItems(assessments);

    return (
        <Card className="overview-ai-summary mb-4">
            <CardHeader className="overview-ai-summary__header">
                <Row className="g-3 align-items-center">
                    <Col xs="12" md="6" lg="3">
                        <div className="overview-ai-summary__meta-item">
                            <i className="ri-file-text-line overview-ai-summary__meta-icon" />
                            <div>
                                <small className="overview-ai-summary__meta-label">
                                    {t("Report Type")}
                                </small>
                                <div className="overview-ai-summary__meta-value">
                                    {t("Software Asset Report")}
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col xs="12" md="6" lg="3">
                        <div className="overview-ai-summary__meta-item">
                            <i className="ri-computer-line overview-ai-summary__meta-icon" />
                            <div>
                                <small className="overview-ai-summary__meta-label">
                                    {t("Software asset")}
                                </small>
                                <div className="overview-ai-summary__meta-value">
                                    {softwareData?.service || "—"}
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col xs="12" md="6" lg="3">
                        <div className="overview-ai-summary__meta-item">
                            <i className="ri-shield-check-line overview-ai-summary__meta-icon" />
                            <div>
                                <small className="overview-ai-summary__meta-label">
                                    {t("Overall Risk Score")}
                                </small>
                                <div
                                    className="overview-ai-summary__meta-value"
                                    style={{ color: privacyScore.color }}
                                >
                                    {t(privacyScore.title)}
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col xs="12" md="6" lg="3">
                        <div className="overview-ai-summary__meta-item">
                            <i className="ri-calendar-line overview-ai-summary__meta-icon" />
                            <div>
                                <small className="overview-ai-summary__meta-label">
                                    {t("Created On")}
                                </small>
                                <div className="overview-ai-summary__meta-value">
                                    {createdOn
                                        ? dateUtils.convertDateToDate(
                                            createdOn,
                                            INTERNATIONAL_DATE_FORMAT
                                        )
                                        : "—"}
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>
            </CardHeader>

            <CardBody>
                <Row className="g-4">
                    <Col xs="12" lg="7">
                        <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                            <div>
                                <h6 className="overview-section-title mb-1">
                                    {t("AI-generated Summary")}
                                </h6>
                                <small className="text-muted d-block">
                                    {t(
                                        "Generated from the current compliance assessment data."
                                    )}
                                </small>
                            </div>
                            {generatingType === "compliance_summary" && (
                                <Spinner size="sm" />
                            )}
                        </div>

                        {(summary.status === "stale" ||
                            summary.status === "missing" ||
                            summary.status === "failed") && (
                            <div className="overview-ai-summary__cue mb-3">
                                <span>
                                    {summary.status === "stale"
                                        ? t(
                                            "Based on previous compliance data."
                                        )
                                        : t(
                                            "AI summary is not up to date."
                                        )}
                                </span>
                                <Button
                                    color="link"
                                    size="sm"
                                    className="p-0 ms-2"
                                    disabled={generateMutation.isLoading}
                                    onClick={() =>
                                        generateMutation.mutate({
                                            type: "compliance_summary",
                                            force: true
                                        })
                                    }
                                >
                                    {t("Refresh")}
                                </Button>
                            </div>
                        )}

                        {(summary.source === "fallback" || degraded) && (
                            <small className="text-muted d-block mb-2">
                                {t("Template summary")}
                            </small>
                        )}

                        {summary.headline && (
                            <p className="fw-semibold mb-3">
                                {summary.headline}
                            </p>
                        )}

                        <div className="overview-ai-summary__text">
                            {summaryParagraphs.map((paragraph, index) => (
                                <p key={index} className="text-muted mb-3">
                                    {paragraph}
                                </p>
                            ))}
                        </div>

                        {highlights.length > 0 && (
                            <ul className="overview-ai-summary__highlights">
                                {highlights.map((item, index) => (
                                    <li key={index}>{item}</li>
                                ))}
                            </ul>
                        )}
                    </Col>

                    <Col xs="12" lg="5">
                        <div className="overview-ai-summary__risk-analysis mb-4">
                            <div className="d-flex align-items-center justify-content-center gap-2 mb-3">
                                <h6 className="overview-section-title mb-0">
                                    {t("Privacy Risk Analysis")}
                                </h6>
                                {generatingType ===
                                    "privacy_risk_analysis" && (
                                    <Spinner size="sm" />
                                )}
                            </div>
                            <small className="text-muted d-block mb-3">
                                {t(
                                    "AI explanation based on the deterministic privacy-risk assessment."
                                )}
                            </small>

                            {(privacyRiskAnalysis.status === "stale" ||
                                privacyRiskAnalysis.status === "missing" ||
                                privacyRiskAnalysis.status === "failed") && (
                                <div className="overview-ai-summary__cue mb-3">
                                    <span>
                                        {privacyRiskAnalysis.status ===
                                        "stale"
                                            ? t(
                                                "Based on previous compliance data."
                                            )
                                            : t(
                                                "AI summary is not up to date."
                                            )}
                                    </span>
                                    <Button
                                        color="link"
                                        size="sm"
                                        className="p-0 ms-2"
                                        disabled={generateMutation.isLoading}
                                        onClick={() =>
                                            generateMutation.mutate({
                                                type: "privacy_risk_analysis",
                                                force: true
                                            })
                                        }
                                    >
                                        {t("Refresh")}
                                    </Button>
                                </div>
                            )}

                            <div className="overview-ai-summary__gauge-image">
                                <img
                                    src={gauge.image}
                                    alt={t("Privacy Risk Analysis")}
                                />
                            </div>

                            {isNotAssessed ? (
                                <div className="mt-3">
                                    <div
                                        className="overview-ai-summary__risk-level-label"
                                        style={{ color: gauge.color }}
                                    >
                                        {t("Not Assessed")}
                                    </div>
                                    <Button
                                        color="primary"
                                        outline
                                        size="sm"
                                        className="mt-2"
                                        onClick={() =>
                                            history.push({
                                                search: "?tab=assessments&assessment=processing-activity"
                                            })
                                        }
                                    >
                                        {t("Complete Processing Activity")}
                                    </Button>
                                </div>
                            ) : (
                                <div
                                    className="overview-ai-summary__risk-level-label"
                                    style={{ color: gauge.color }}
                                >
                                    {t(gauge.label)}
                                </div>
                            )}

                            {privacyRiskAnalysis.analysis && (
                                <p className="text-muted mt-3 mb-0">
                                    {privacyRiskAnalysis.analysis}
                                </p>
                            )}
                        </div>

                        <div className="overview-ai-summary__verification">
                            <h6 className="overview-section-title mb-3">
                                {t("Risk Assessment")}
                            </h6>
                            <ol className="list-unstyled mb-0 overview-ai-summary__assessment-list">
                                {assessmentItems.map((item, index) => (
                                    <li
                                        key={item.id}
                                        className="overview-ai-summary__verification-item"
                                    >
                                        <span className="overview-ai-summary__verification-label">
                                            {`${index + 1}. ${t(item.label)}`}
                                        </span>
                                        <StatusBadge
                                            t={t}
                                            status={item.status}
                                        />
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </Col>
                </Row>
            </CardBody>

            <CardFooter className="overview-ai-summary__footer">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
                    <small className="text-muted mb-0">
                        <i className="ri-information-line me-1" />
                        {t(
                            "AI assists explanation only. Compliance decisions remain system-controlled."
                        )}
                    </small>
                    {generatedAt && (
                        <small className="text-muted mb-0">
                            {t("Last updated")}{" "}
                            {dateUtils.convertDateToDate(
                                generatedAt,
                                INTERNATIONAL_DATE_FORMAT
                            )}{" "}
                            {dateUtils.convertDateToDate(generatedAt, "HH:mm")}
                        </small>
                    )}
                </div>
            </CardFooter>
        </Card>
    );
};

export default withNamespaces()(AiSummary);
